import apiClient from '@/api/client'
import type { ReceiptLoyaltyBlock } from '@/lib/printCustomerReceipt'
import {
  loadBrandLogo,
  renderBrandedQrDataUrl,
  toMonochromeDataUrl,
  QR_LOGO_SCALE_MIN,
} from '@/lib/brandedQr'

/**
 * Size the QR is rendered at when it carries a logo. The receipt renderer
 * draws the loyalty QR at RECEIPT_LOYALTY_QR_PX in printCustomerReceipt.ts,
 * which must agree with this.
 */
const RECEIPT_QR_RENDER_PX = 320

export type ReceiptLoyaltyBrandOptions = {
  /** `receipt_logo_url`, already validated by parseReceiptSettings. */
  logoUrl?: string
  /** Tenant-level `branded_qr_enabled`. */
  enabled?: boolean
}

/**
 * Fetch the per-receipt loyalty claim info for an order and produce
 * the right block payload for the receipt renderer to draw. Returns
 * one of three tagged variants — or null when loyalty isn't enabled
 * or the fetch fails.
 *
 * Errors are swallowed by design — a missing loyalty block must
 * never prevent a receipt from printing.
 *
 *   • 'saved'          — cashier captured the phone; receipt prints
 *                        progress text only (no QR).
 *   • 'unsaved'        — no phone yet, but the public URL is real;
 *                        receipt prints "SCAN TO SAVE" + working QR.
 *   • 'unsaved_no_url' — no phone, and the public URL isn't
 *                        configured (localhost / LAN); receipt prints
 *                        a fallback CTA, NO QR (it would be dead
 *                        paper — customer phones can't reach the URL).
 *
 * Called from the counter's payment-completion path right before the
 * slip is dispatched to the printer.
 */
export async function buildReceiptLoyaltyBlock(
  orderId: string,
  brand?: ReceiptLoyaltyBrandOptions,
): Promise<ReceiptLoyaltyBlock | null> {
  try {
    const res = await apiClient.getOrderLoyaltyClaim(orderId)
    if (!res.success || !res.data || !res.data.enabled) return null
    const {
      url,
      claim_code,
      punches,
      stamps_earned,
      threshold,
      reward_label,
      already_attached,
      is_scannable,
      receipt_headline,
      receipt_progress_message,
      customer_name,
      customer_phone,
    } = res.data
    const rewardLabel = (reward_label || 'reward').toString()
    // Admin-customised copy carries through to the renderer; empty
    // strings are normalised to undefined so the renderer's fallback
    // path stays simple (`(custom || '').trim() !== ''`).
    const customHeadline = receipt_headline || undefined
    const customProgress = receipt_progress_message || undefined

    // ── State 1: visit already saved ─────────────────────────────
    if (already_attached) {
      return {
        state: 'saved',
        punches,
        stampsEarned: stamps_earned,
        threshold,
        rewardLabel,
        customHeadline,
        customProgress,
        // Echoed on the slip ("Awarded to Maryam · ····1234") so the guest
        // can verify the visit landed on the right name / number. Empty
        // strings normalise to undefined so the renderer skips cleanly.
        guestName: (customer_name || '').trim() || undefined,
        guestPhone: (customer_phone || '').trim() || undefined,
      }
    }

    // ── State 3: not saved AND public URL not configured ────────
    // Skip QR generation entirely — printing it would be dead paper.
    if (!is_scannable || !url) {
      return {
        state: 'unsaved_no_url',
        threshold,
        rewardLabel,
        customHeadline,
      }
    }

    // ── State 2: not saved + scannable URL ──────────────────────
    // Thermal printers are 1-bit: every pixel is ink or no ink, and the driver
    // dithers anything between. A dithered colour logo scatters stray dots
    // across the modules the scanner is trying to read, so the logo is
    // collapsed to pure black/white first. The smallest allowed logo is used —
    // this is the least forgiving surface we print a QR on, and the guest is
    // scanning a 3cm code off crinkled paper.
    let logoDataUrl: string | null = null
    if (brand?.enabled !== false && brand?.logoUrl) {
      const raw = await loadBrandLogo(brand.logoUrl)
      if (raw) logoDataUrl = await toMonochromeDataUrl(raw)
    }

    const qrDataUri = await renderBrandedQrDataUrl({
      data: url,
      size: RECEIPT_QR_RENDER_PX,
      style: {
        dark_color: '#000000',
        light_color: '#ffffff',
        margin_modules: 1,
        dots_type: 'square',
        corner_square_type: 'square',
        corner_dot_type: 'square',
        error_correction: 'M',
      },
      logoDataUrl,
      logoEnabled: Boolean(logoDataUrl),
      logoScale: QR_LOGO_SCALE_MIN,
    })
    return {
      state: 'unsaved',
      url,
      qrDataUri,
      stampsEarned: stamps_earned,
      threshold,
      rewardLabel,
      customHeadline,
      claimCode: (claim_code || '').trim() || undefined,
    }
  } catch {
    return null
  }
}
